/**
 * ForceLayout - Pure D3 force simulation (D3 owns DOM)
 *
 * Matches the existing ForceDirectedGraph.js pattern: D3 creates all
 * SVG elements inside a ref container. React only provides the <svg> shell.
 */

import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Box, useTheme } from '@mui/material';
import { ZONE_CONFIG, EGO_CONFIG, FORCE_LAYOUT } from '../../constants/dashboardConfig';
import { getInitials, getConnectionStrength } from '../../utils/networkUtils';

// eslint-disable-next-line max-lines-per-function
function ForceLayout({
  nodes,
  edges,
  egoId,
  selectedNode,
  selectedEdge,
  onNodeSelect,
  onEdgeSelect,
  onDeselect,
}) {
  const svgRef = useRef(null);
  const theme = useTheme();

  // Store callbacks in refs so D3 closures always see latest
  const callbacksRef = useRef({ onNodeSelect, onEdgeSelect, onDeselect });
  useEffect(() => {
    callbacksRef.current = { onNodeSelect, onEdgeSelect, onDeselect };
  }, [onNodeSelect, onEdgeSelect, onDeselect]);

  // Store selection in ref for D3 access
  const selectionRef = useRef({ selectedNode, selectedEdge });
  useEffect(() => {
    selectionRef.current = { selectedNode, selectedEdge };
  }, [selectedNode, selectedEdge]);

  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    if (!nodes?.length || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = svgRef.current.clientWidth || 900;
    const height = svgRef.current.clientHeight || 700;

    svg.attr('viewBox', [0, 0, width, height]);

    // Prepare data (deep copy for D3 mutation)
    const simNodes = nodes.map((n) => ({
      ...n,
      initials: getInitials(n.label),
    }));
    const simEdges = edges.map((e) => ({
      ...e,
      strength: getConnectionStrength(e.evidence_count || e.weight || 1),
    }));

    // Container with zoom
    const container = svg.append('g');
    const zoom = d3.zoom()
      .scaleExtent([0.3, 4])
      .on('zoom', (event) => container.attr('transform', event.transform));
    svg.call(zoom);

    // Click on background to deselect
    svg.on('click', () => callbacksRef.current.onDeselect());

    // Force simulation
    const simulation = d3.forceSimulation(simNodes)
      .force(
        'link',
        d3.forceLink(simEdges)
          .id((d) => d.id)
          .distance(FORCE_LAYOUT.linkDistance)
      )
      .force('charge', d3.forceManyBody().strength(FORCE_LAYOUT.chargeStrength))
      .force('center', d3.forceCenter(width / 2, height / 2).strength(FORCE_LAYOUT.centerStrength))
      .force('collision', d3.forceCollide().radius(FORCE_LAYOUT.collisionRadius))
      .alphaDecay(FORCE_LAYOUT.alphaDecay);

    // Draw edges
    const link = container.append('g')
      .selectAll('line')
      .data(simEdges)
      .join('line')
      .attr('stroke', (d) => {
        if (d.strength === 'strong') return 'rgba(35, 163, 143, 0.35)';
        if (d.strength === 'medium') return 'rgba(0, 151, 178, 0.25)';
        return 'rgba(16, 3, 49, 0.12)';
      })
      .attr('stroke-width', (d) => {
        if (d.strength === 'strong') return 2.5;
        if (d.strength === 'medium') return 1.8;
        return 1.2;
      })
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        callbacksRef.current.onEdgeSelect({
          source: typeof d.source === 'object' ? d.source.id : d.source,
          target: typeof d.target === 'object' ? d.target.id : d.target,
          ...d,
        });
      });

    // Draw nodes
    const node = container.append('g')
      .selectAll('g')
      .data(simNodes)
      .join('g')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        callbacksRef.current.onNodeSelect(d);
      })
      .call(drag(simulation));

    // Node circles
    node.append('circle')
      .attr('r', (d) => (d.isEgo ? 36 : 26))
      .attr('fill', (d) => {
        if (d.isEgo) return EGO_CONFIG.gradient[0];
        const zoneConf = ZONE_CONFIG[d.zone];
        return zoneConf ? zoneConf.color : '#767676';
      })
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))');

    // Node initials
    node.append('text')
      .text((d) => d.initials)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('fill', 'white')
      .attr('font-size', (d) => (d.isEgo ? 16 : 12))
      .attr('font-weight', 700)
      .attr('font-family', 'DM Sans, sans-serif')
      .style('pointer-events', 'none');

    // Node name labels
    node.append('text')
      .text((d) => d.label)
      .attr('text-anchor', 'middle')
      .attr('dy', (d) => (d.isEgo ? 48 : 38))
      .attr('fill', theme.palette.text.primary)
      .attr('font-size', 11)
      .attr('font-weight', 600)
      .attr('font-family', 'DM Sans, sans-serif')
      .style('pointer-events', 'none');

    // Tick handler
    simulation.on('tick', () => {
      link
        .attr('x1', (d) => d.source.x)
        .attr('y1', (d) => d.source.y)
        .attr('x2', (d) => d.target.x)
        .attr('y2', (d) => d.target.y);

      node.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function drag(sim) {
      return d3.drag()
        .on('start', (event, d) => {
          if (!event.active) sim.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) sim.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        });
    }

    return () => {
      simulation.stop();
    };
  }, [nodes, edges, egoId, theme]);

  // Update visual selection state without re-running simulation
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);

    // Highlight selected node
    svg.selectAll('g > g > g').each(function (d) {
      const g = d3.select(this);
      const isSelected = selectedNode?.id === d?.id;
      g.select('circle')
        .attr('stroke', isSelected ? '#23A38F' : 'white')
        .attr('stroke-width', isSelected ? 3 : 2);
      g.style('opacity', !selectedNode || isSelected ? 1 : 0.5);
    });
  }, [selectedNode, selectedEdge]);

  return (
    <Box sx={{ width: '100%', height: '100%', pb: 7 }}>
      <svg
        ref={svgRef}
        role="img"
        aria-label="Force-directed ego network visualization"
        style={{ width: '100%', height: '100%' }}
      />
    </Box>
  );
}

export default ForceLayout;
